import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent } from './ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './ui/popover';
import {
    Bell,
    Settings,
    LogOut,
    User,
    Sun,
    Moon,
    Monitor,
    ChevronDown,
    Check,
    CheckCheck,
    ExternalLink,
    Calendar,
    MessageSquare,
    CheckSquare,
    Palmtree,
    Users,
    Trophy,
    BookOpen,
    X
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';

const HeaderBubble = () => {
    const { user, logout, api } = useAuth();
    const { theme, setTheme, resolvedTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [notificationOpen, setNotificationOpen] = useState(false);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const [handbuchOpen, setHandbuchOpen] = useState(false);

    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const res = await api.get('/notifications/unread-count');
                setUnreadCount(res.data.count || 0);
            } catch (e) {}
        };
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, [api]);

    const fetchNotifications = async () => {
        setLoadingNotifications(true);
        try {
            const res = await api.get('/notifications?limit=10');
            setNotifications(res.data || []);
        } catch (e) {
            console.error('Error fetching notifications:', e);
        } finally {
            setLoadingNotifications(false);
        }
    };

    const handleOpenNotifications = async () => {
        setNotificationOpen(true);
        await fetchNotifications();
        // Auto-mark all as read when popup is opened
        if (unreadCount > 0) {
            try {
                await api.put('/notifications/read-all');
                setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                setUnreadCount(0);
            } catch (e) {
                console.error('Error marking notifications as read:', e);
            }
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await api.put(`/notifications/${notificationId}/read`);
            setNotifications(prev => prev.map(n => 
                n.id === notificationId ? { ...n, is_read: true } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (e) {}
    };

    const markAllAsRead = async () => {
        try {
            await api.put('/notifications/read-all');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
            toast.success('Alle als gelesen markiert');
        } catch (e) {
            toast.error('Fehler beim Markieren');
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.is_read) {
            await markAsRead(notification.id);
        }
        setNotificationOpen(false);
        if (notification.link) {
            navigate(notification.link);
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'task_assigned':
            case 'task_completed':
            case 'task_comment':
                return <CheckSquare className="w-4 h-4 text-primary" />;
            case 'calendar_invite':
            case 'calendar_reminder':
                return <Calendar className="w-4 h-4 text-emerald-500" />;
            case 'new_message':
                return <MessageSquare className="w-4 h-4 text-blue-500" />;
            case 'vacation_request':
            case 'vacation_approved':
            case 'vacation_rejected':
                return <Palmtree className="w-4 h-4 text-amber-500" />;
            case 'project_added':
                return <Users className="w-4 h-4 text-purple-500" />;
            case 'challenge':
                return <Trophy className="w-4 h-4 text-yellow-500" />;
            default:
                return <Bell className="w-4 h-4 text-muted-foreground" />;
        }
    };

    const getInitials = (first, last) => {
        return `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase();
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const toggleTheme = () => {
        if (theme === 'light') setTheme('dark');
        else if (theme === 'dark') setTheme('system');
        else setTheme('light');
    };

    const ThemeIcon = theme === 'system' ? Monitor : resolvedTheme === 'light' ? Sun : Moon;

    return (
        <>
            {/* Desktop: Floating bubble in top-right */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="hidden md:flex fixed top-4 right-4 z-[60] items-center gap-2"
                data-testid="header-bubble"
            >
                {/* Handbuch Button */}
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setHandbuchOpen(true)}
                    className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all shadow-lg backdrop-blur-xl ${
                        handbuchOpen || location.pathname === '/handbuch'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-card/80 hover:bg-card border border-border/50 text-foreground'
                    }`}
                    data-testid="header-handbuch"
                    title="Handbuch"
                >
                    <BookOpen className="w-5 h-5" />
                </motion.button>

                {/* Notifications Popup */}
                <Popover open={notificationOpen} onOpenChange={setNotificationOpen}>
                    <PopoverTrigger asChild>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleOpenNotifications}
                            className={`relative w-11 h-11 rounded-2xl flex items-center justify-center transition-all shadow-lg backdrop-blur-xl ${
                                location.pathname === '/notifications' || notificationOpen
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-card/80 hover:bg-card border border-border/50 text-foreground'
                            }`}
                            data-testid="header-notifications"
                        >
                            <Bell className="w-5 h-5" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] flex items-center justify-center font-bold shadow-md animate-pulse">
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </motion.button>
                    </PopoverTrigger>
                    <PopoverContent 
                        align="end" 
                        sideOffset={8} 
                        className="w-96 p-0 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b bg-muted/30">
                            <div className="flex items-center gap-2">
                                <Bell className="w-4 h-4 text-primary" />
                                <span className="font-semibold">Benachrichtigungen</span>
                                {unreadCount > 0 && (
                                    <span className="px-1.5 py-0.5 rounded-full bg-destructive text-destructive-foreground text-xs font-bold">
                                        {unreadCount}
                                    </span>
                                )}
                            </div>
                            {unreadCount > 0 && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-7 text-xs"
                                    onClick={markAllAsRead}
                                >
                                    <CheckCheck className="w-3.5 h-3.5 mr-1" />
                                    Alle gelesen
                                </Button>
                            )}
                        </div>

                        {/* Notifications List */}
                        <ScrollArea className="max-h-[400px]">
                            {loadingNotifications ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : notifications.length > 0 ? (
                                <div className="divide-y">
                                    {notifications.map((notification) => (
                                        <div
                                            key={notification.id}
                                            onClick={() => handleNotificationClick(notification)}
                                            className={`flex items-start gap-3 p-3 cursor-pointer transition-colors hover:bg-muted/50 ${
                                                !notification.is_read ? 'bg-primary/5' : ''
                                            }`}
                                        >
                                            <div className={`mt-0.5 p-2 rounded-lg ${!notification.is_read ? 'bg-primary/10' : 'bg-muted'}`}>
                                                {getNotificationIcon(notification.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm truncate ${!notification.is_read ? 'font-medium' : ''}`}>
                                                    {notification.title}
                                                </p>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {notification.message}
                                                </p>
                                                <p className="text-[10px] text-muted-foreground mt-1">
                                                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: de })}
                                                </p>
                                            </div>
                                            {!notification.is_read && (
                                                <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                    <p className="text-sm">Keine Benachrichtigungen</p>
                                </div>
                            )}
                        </ScrollArea>

                        {/* Footer */}
                        <div className="p-2 border-t bg-muted/30">
                            <Button 
                                variant="ghost" 
                                className="w-full h-9 text-sm"
                                onClick={() => { setNotificationOpen(false); navigate('/notifications'); }}
                            >
                                Alle anzeigen
                                <ExternalLink className="w-3.5 h-3.5 ml-2" />
                            </Button>
                        </div>
                    </PopoverContent>
                </Popover>

                {/* System (Admin only) */}
                {user?.role === 'chef' && (
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/system')}
                        className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all shadow-lg backdrop-blur-xl ${
                            location.pathname === '/system'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-card/80 hover:bg-card border border-border/50 text-foreground'
                        }`}
                        data-testid="header-system"
                    >
                        <Settings className="w-5 h-5" />
                    </motion.button>
                )}

                {/* Theme Toggle */}
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={toggleTheme}
                    className="w-11 h-11 rounded-2xl flex items-center justify-center bg-card/80 hover:bg-card border border-border/50 shadow-lg backdrop-blur-xl transition-all"
                    data-testid="header-theme"
                    title={theme === 'system' ? `System (${resolvedTheme})` : theme}
                >
                    <ThemeIcon className="w-5 h-5" />
                </motion.button>

                {/* User Menu */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex items-center gap-2 h-11 pl-1.5 pr-3 rounded-2xl bg-card/80 hover:bg-card border border-border/50 shadow-lg backdrop-blur-xl transition-all"
                            data-testid="header-user-menu"
                        >
                            <Avatar className="w-8 h-8">
                                {user?.profile_image ? (
                                    <AvatarImage src={user.profile_image} />
                                ) : (
                                    <AvatarFallback 
                                        style={{ backgroundColor: user?.profile_color || '#009dde' }}
                                        className="text-white text-sm font-medium"
                                    >
                                        {getInitials(user?.first_name, user?.last_name)}
                                    </AvatarFallback>
                                )}
                            </Avatar>
                            <span className="text-sm font-medium hidden sm:block max-w-[100px] truncate">
                                {user?.first_name}
                            </span>
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        </motion.button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                        align="end" 
                        sideOffset={8}
                        className="w-56 z-[100]"
                    >
                        <div className="px-3 py-2 border-b border-border/50">
                            <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                        </div>
                        <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="header-settings">
                            <Settings className="w-4 h-4 mr-2" />
                            Einstellungen
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="header-profile">
                            <User className="w-4 h-4 mr-2" />
                            Profil
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="header-logout">
                            <LogOut className="w-4 h-4 mr-2" />
                            Abmelden
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </motion.div>

            {/* Mobile: Compact icons inside the mobile header bar (positioned inside Sidebar's mobile header) */}
            <div 
                className="md:hidden fixed top-0 right-2 z-[51] flex items-center gap-1"
                style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(56px + env(safe-area-inset-top))' }}
                data-testid="header-bubble-mobile"
            >
                {/* Handbuch */}
                <button
                    onClick={() => setHandbuchOpen(true)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                        handbuchOpen || location.pathname === '/handbuch'
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted/50 text-foreground'
                    }`}
                    data-testid="header-handbuch-mobile"
                >
                    <BookOpen className="w-4 h-4" />
                </button>

                {/* Notifications */}
                <button
                    onClick={() => navigate('/notifications')}
                    className={`relative w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                        location.pathname === '/notifications'
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted/50 text-foreground'
                    }`}
                    data-testid="header-notifications-mobile"
                >
                    <Bell className="w-4 h-4" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-destructive text-destructive-foreground text-[9px] flex items-center justify-center font-bold">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>

                {/* System (Admin only) */}
                {user?.role === 'chef' && (
                    <button
                        onClick={() => navigate('/system')}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                            location.pathname === '/system'
                                ? 'bg-primary text-primary-foreground'
                                : 'hover:bg-muted/50 text-foreground'
                        }`}
                        data-testid="header-system-mobile"
                    >
                        <Settings className="w-4 h-4" />
                    </button>
                )}

                {/* Theme Toggle */}
                <button
                    onClick={toggleTheme}
                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted/50 transition-all"
                    data-testid="header-theme-mobile"
                >
                    <ThemeIcon className="w-4 h-4" />
                </button>

                {/* User Avatar - simplified for mobile */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden"
                            data-testid="header-user-menu-mobile"
                        >
                            <Avatar className="w-7 h-7">
                                {user?.profile_image ? (
                                    <AvatarImage src={user.profile_image} />
                                ) : (
                                    <AvatarFallback 
                                        style={{ backgroundColor: user?.profile_color || '#009dde' }}
                                        className="text-white text-[10px] font-medium"
                                    >
                                        {getInitials(user?.first_name, user?.last_name)}
                                    </AvatarFallback>
                                )}
                            </Avatar>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                        align="end" 
                        sideOffset={8}
                        className="w-56 z-[100]"
                    >
                        <div className="px-3 py-2 border-b border-border/50">
                            <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                        </div>
                        <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="header-settings-mobile">
                            <Settings className="w-4 h-4 mr-2" />
                            Einstellungen
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="header-profile-mobile">
                            <User className="w-4 h-4 mr-2" />
                            Profil
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="header-logout-mobile">
                            <LogOut className="w-4 h-4 mr-2" />
                            Abmelden
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Handbuch Modal */}
            <HandbuchModal open={handbuchOpen} onOpenChange={setHandbuchOpen} />
        </>
    );
};

// Handbuch Modal Component - Two Column Layout
const HandbuchModal = ({ open, onOpenChange }) => {
    const [activeChapter, setActiveChapter] = useState('ai-assistant');
    const [searchTerm, setSearchTerm] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const chapters = [
        {
            id: 'ai-assistant',
            icon: '🤖',
            title: 'KI-Assistent',
            color: '#8B5CF6',
            content: [
                { title: '🤖 Was ist der KI-Assistent?', text: 'Dein intelligenter Arbeitsassistent wie Siri oder Alexa - aber für AgencyOS! Sprich oder tippe einfach was du brauchst.' },
                { title: '🎤 Spracheingabe', text: 'Mikrofon-Button gedrückt halten → Sprechen → Loslassen. Deine Sprache wird automatisch erkannt.' },
                { title: '📝 Tasks erstellen', text: '• "Erstelle Task Website Banner für Lisa"\n• "Neue Aufgabe Social Media Post bis Freitag"\n• "Task Report für Kunde Müller mit hoher Priorität"\n• "Erstelle wöchentlichen Task Team-Meeting"\n• "Task Logo-Design mit Account Manager Max"' },
                { title: '🔄 Wiederkehrende Tasks', text: '• "Erstelle täglichen Task E-Mails checken"\n• "Wöchentlicher Task Reporting jeden Montag"\n• "Monatlicher Task Rechnung an Kunde X"\n• Muster: täglich, wöchentlich, alle 2 Wochen, monatlich\n\n→ Wenn der Task erledigt wird, erstellt sich automatisch der nächste!' },
                { title: '✏️ Tasks bearbeiten', text: '• "Setze Task Banner auf Priorität hoch"\n• "Verschiebe Task Website auf nächste Woche"\n• "Weise Task Logo Max zu"\n• "Markiere Task Report als erledigt"' },
                { title: '🔍 Tasks suchen', text: '• "Zeig meine offenen Tasks"\n• "Welche Aufgaben habe ich heute?"\n• "Überfällige Tasks"\n• "Tasks von Projekt Website"' },
                { title: '📅 Kalender-Events', text: '• "Meeting morgen um 14 Uhr"\n• "Kundengespräch mit Müller am Freitag 10 Uhr"\n• "Was steht heute im Kalender?"\n• "Termine diese Woche"' },
                { title: '📁 Projekte', text: '• "Erstelle Projekt für Kunde Müller"\n• "Neues Projekt Website Relaunch mit 3 Tasks"\n• "Zeig alle aktiven Projekte"' },
                { title: '⏱️ Zeiterfassung', text: '• "Starte Zeit für Website Design"\n• "Stopp die Zeit"\n• "Buche 2 Stunden auf Projekt XY"\n• "Wie viele Stunden diese Woche?"' },
                { title: '🌴 Urlaub', text: '• "Beantrage Urlaub vom 12. bis 20. Juli"\n• "Wer ist im Urlaub?"\n• "Wie viele Urlaubstage habe ich noch?"' },
                { title: '📊 Zusammenfassungen', text: '• "Was steht heute an?"\n• "Was ist am wichtigsten?"\n• "Deadlines diese Woche"\n• "Wie viele Punkte habe ich?"' },
                { title: '👥 Team & Workload', text: '• "Wer arbeitet an Projekt XY?"\n• "Wer ist heute im Büro?"\n• "Zeig mir die Workload von Max"\n• "Wie ausgelastet ist Lisa?"\n• "Zeig mir meine Workload"' },
                { title: '🎭 Social Faces suchen', text: '• "Suche blonde Frauen ohne Tattoos"\n• "Finde Männer mit braunen Haaren aus Wien"\n• "Zeig mir vegetarische Models"\n\nKlick auf Ergebnis öffnet das vollständige Profil.' },
                { title: '🏢 Projekte nach Kunde', text: '• "Zeig Projekte von Kunde Müller"\n• "Welche Projekte hat Firma XY?"\n• "Aktive Projekte für BMW"' },
                { title: '💡 Tipps', text: '• Sprich natürlich - der Assistent versteht dich!\n• Datumsbegriffe: "morgen", "nächste Woche", "Freitag", "in 3 Tagen"\n• Prioritäten: "wichtig", "dringend", "hoch" = Hohe Priorität\n• Selbstzuweisung: "mir", "mir selbst", "für mich"' }
            ]
        },
        {
            id: 'tasks',
            icon: '✅',
            title: 'Tasks',
            color: '#34C759',
            content: [
                { title: '📋 Kanban-Board', text: 'Spalten: Offen, In Bearbeitung, Review, Erledigt. Drag & Drop zwischen Spalten.' },
                { title: '➕ Task erstellen', text: 'Titel, Beschreibung, Status, Priorität (Hoch/Mittel/Niedrig), Deadline, Zuweisungen, Projekt, Account Manager.' },
                { title: '🔄 Wiederkehrende Tasks', text: 'Muster wählen: Täglich, Wöchentlich, Alle 2 Wochen, Monatlich.\n\n→ Funktionsweise:\n1. Task mit Wiederholung erstellen\n2. Task als "Erledigt" markieren\n3. System erstellt automatisch neuen Task mit nächstem Datum!\n\nBeispiel: Wöchentlicher Report (Deadline 20.03.) → Erledigt → Neuer Task erscheint mit Deadline 27.03.' },
                { title: '👤 Account Manager', text: 'Jedem Task kann ein Account Manager zugewiesen werden. Dieser wird bei Änderungen benachrichtigt.' },
                { title: '💬 Kommentare & Dateien', text: 'Team-Kommunikation zum Task. Dateien per Drag & Drop hochladen.' },
                { title: '🎨 Custom Status (Admin)', text: 'Eigene Status erstellen mit Farbe und Emoji. Reihenfolge per Drag & Drop.' }
            ]
        },
        {
            id: 'time',
            icon: '⏱️',
            title: 'Zeiterfassung',
            color: '#5856D6',
            content: [
                { title: '▶️ Live-Timer', text: 'Play-Button drücken → Timer läuft → Stop-Button → Kunde/Task/Aktivität wählen → Speichern.' },
                { title: '⏸️ Stop-Modal Optionen', text: '• Pause: Pausenzeit erfassen\n• Allgemein: Zeit ohne Kundenzuordnung\n• Auf Kunde buchen: Kunde + Task + Aktivitätstyp' },
                { title: '🎯 Aktivitätstypen', text: 'Grafik, Videoschnitt, Reporting, Storyboard, Entwicklung, Meeting, Planung, Support.' },
                { title: '📝 Manuelle Einträge', text: 'Zeiten → + Manueller Eintrag → Startzeit, Endzeit, Kunde, Beschreibung.' },
                { title: '📥 Export-Funktion', text: '• Export-Button → Zeitraum auswählen\n• Download als CSV-Datei\n• Öffnet sich in Excel und Google Sheets' },
                { title: '🔒 Sperrfristen', text: 'Einträge älter als 2 Monate sind gesperrt. Nur Chef kann diese bearbeiten.' }
            ]
        },
        {
            id: 'projects',
            icon: '📁',
            title: 'Projekte',
            color: '#FF2D55',
            content: [
                { title: '📁 Projekt erstellen', text: 'Name, Kunde, Beschreibung, Deadline, Team, CI-Farben, Logo.' },
                { title: '🎯 Meilensteine', text: 'Titel, Beschreibung, Deadline. Status: Offen / Erledigt. Progress wird automatisch berechnet.' },
                { title: '📎 Dateien & Links', text: 'Dateien hochladen (Cloudflare R2). Links zu Google Drive, Figma, etc.' },
                { title: '⏱️ Zeit-Statistik', text: 'Gesamt gebuchte Stunden und Aufschlüsselung nach Teammitglied.' }
            ]
        },
        {
            id: 'calendar',
            icon: '📅',
            title: 'Kalender',
            color: '#FF3B30',
            content: [
                { title: '📅 Ansichten', text: '• Wochenansicht: 7 Tage horizontal, Drag & Drop\n• Monatsansicht: Vollständiger Monat\n• Tagesansicht: 24-Stunden-Timeline' },
                { title: '➕ Event erstellen', text: 'Titel, Ganztägig, Start/Ende, Ort, Beschreibung, Wiederholen, Erinnerung, Farbe, Teilnehmer, Zielkalender.' },
                { title: '⚡ Event-Vorlagen', text: 'Daily Standup (15 Min), Kundencall (60 Min), Team Meeting (60 Min), Code Review (30 Min).' },
                { title: '🔔 Erinnerungen', text: 'Keine, 5 Min, 15 Min, 30 Min, 1 Stunde, 1 Tag vorher.' },
                { title: '👥 Verfügbarkeit', text: 'Teilnehmer auswählen → Rot = Besetzt, Grün = Frei. "Alle frei"-Zeile zeigt gemeinsame Slots.' },
                { title: '🔄 Mehrere Kalender', text: '• Verbinde mehrere iCloud-Kalender gleichzeitig\n• Jeder Kalender kann eine eigene Farbe bekommen\n• Beim Event erstellen: Zielkalender auswählen\n• Urlaubs-Kalender festlegen für automatische Urlaubseinträge' },
                { title: '☁️ iCloud verbinden', text: '1. Einstellungen → Kalender → iCloud wählen\n2. Apple-ID E-Mail eingeben\n3. App-spezifisches Passwort erstellen:\n   → appleid.apple.com → Anmelden → App-spezifische Passwörter\n   → Passwort generieren (Name z.B. "AgencyOS")\n   → Das Passwort wird MIT BINDESTRICHEN angezeigt (z.B. xxxx-xxxx-xxxx-xxxx)\n   → WICHTIG: Passwort genau so MIT BINDESTRICHEN eingeben!\n4. "Kalender laden" klicken\n5. Gewünschte Kalender auswählen\n6. Fertig!' },
                { title: '🔗 Google Calendar', text: '1. Einstellungen → Kalender → Google wählen\n2. "Mit Google verbinden" klicken\n3. Google-Konto auswählen und Zugriff erlauben\n4. Fertig!' }
            ]
        },
        {
            id: 'forms',
            icon: '🧾',
            title: 'Formulare',
            color: '#EF4444',
            content: [
                { title: '📄 Was sind Formulare?', text: 'Digitale Erfassung verschiedener Formulare im Wizard-Format. QR-Codes für schnellen mobilen Zugriff.' },
                { title: '📸 Shooting-Dokumentation', text: '1. Datum des Shootings\n2. Uhrzeit (Von - Bis)\n3. Team vor Ort (Name, Position)\n4. Social Faces mit Check-In/Check-Out\n5. Notizen & Anmerkungen\n6. Zusammenfassung pruefen & absenden' },
                { title: '✅ Shooting-Zusage', text: 'Für Kunden: Shooting-Teilnahme bestätigen mit digitaler Unterschrift.\nÖffentlicher Link mit QR-Code.' },
                { title: '📝 Einverständniserklärung', text: 'Für Firmen: Mitarbeiter-Daten für Shootings erfassen.\n• Persönliche Daten\n• Kontaktdaten\n• Digitale Unterschrift\n\nQR-Code für schnellen Zugriff.' },
                { title: '📋 Client Intake Form', text: '20-Schritte Formular für Neukunden-Onboarding:\n• Unternehmensdaten & Branche\n• Ansprechpartner & Kontakt\n• Social Media Accounts\n• Zielgruppe & Positionierung\n• Content-Wünsche & Budget\n• CI-Dateien Upload\n\nQR-Code für Kunden zum selbst ausfüllen.' },
                { title: '💰 Spesenabrechnung', text: '1. Datum der Ausgabe\n2. Kategorie (Fahrtkosten, Verpflegung, etc.)\n3. Betrag in Euro\n4. Beleg hochladen (Pflicht)\n5. Kunde zuordnen (optional)\n6. Beschreibung\n7. Zusammenfassung & absenden' },
                { title: '📊 Status-Workflow', text: 'Eingereicht → Genehmigt / Abgelehnt\nBenachrichtigungen bei Statusaenderungen.' },
                { title: '🔗 QR-Codes', text: 'Jedes Formular hat einen QR-Code für mobilen Zugriff.\nAdmin kann QR-Code anzeigen/kopieren.' },
                { title: '📥 PDF-Export', text: 'Jede Einreichung als PDF herunterladen - inkl. Unterschriften und Belege.' }
            ]
        },
        {
            id: 'customers',
            icon: '👥',
            title: 'Kunden',
            color: '#00C7BE',
            content: [
                { title: '👤 Kunde erstellen', text: 'Name, Website, E-Mail, Telefon, Adresse, Paket-Name, Paket-Inhalt.' },
                { title: '🎨 Corporate Identity', text: 'CI-Farben (mehrere möglich), Logos hochladen.' },
                { title: '📱 Social Media', text: 'LinkedIn, Instagram, Facebook, YouTube, TikTok, Twitter.' },
                { title: '📝 Interne Notizen', text: 'Nur für Team sichtbare Notizen zum Kunden.' },
                { title: '🌐 Öffentliche Seite', text: 'Globe-Icon → Public Page aktivieren. Passwortschutz, QR-Code, Content-Plan.' }
            ]
        },
        {
            id: 'vacation',
            icon: '🌴',
            title: 'Urlaub',
            color: '#30D158',
            content: [
                { title: '📝 Urlaub beantragen', text: 'Startdatum, Enddatum, Grund (optional), Vertretung, Notizen.' },
                { title: '✅ Genehmigung', text: '1. Mitarbeiter stellt Antrag\n2. Chef erhält Benachrichtigung\n3. Chef genehmigt/lehnt ab\n4. Urlaubstage werden vom Kontingent abgezogen\n5. Kalendereintrag wird automatisch erstellt' },
                { title: '📊 Team-Kalender', text: 'Übersicht wer wann im Urlaub ist. Überschneidungen erkennen.' }
            ]
        },
        {
            id: 'chat',
            icon: '💬',
            title: 'Chat',
            color: '#007AFF',
            content: [
                { title: '💬 Chat-Typen', text: '• Team-Chat: Für alle, immer oben angepinnt\n• Private Chats: 1:1 Nachrichten\n• Gruppen-Chats: Mehrere Personen' },
                { title: '📎 Funktionen', text: 'Text, Dateien (Drag & Drop), Sprachnachrichten (Mikrofon gedrückt halten), Reaktionen (Emojis).' },
                { title: '📞 Video-Calls', text: 'Kamera-Icon → WebRTC-basiert. Kamera/Mikrofon ein/aus, Bildschirm teilen.' },
                { title: '🔔 Status', text: 'Online (grün), Offline (grau), Tipp-Indikator, Lesebestätigungen (blaue Häkchen).' }
            ]
        },
        {
            id: 'challenges',
            icon: '🏆',
            title: 'Challenges',
            color: '#FFD60A',
            content: [
                { title: '⭐ Punkte verdienen', text: 'Task erledigt: +5 Punkte\nProjekt abgeschlossen: +10 Punkte\nTägliches Einloggen: +1 Punkt' },
                { title: '🏆 Achievements', text: '• Task-Maschine: 50 Tasks abgeschlossen\n• Zeit-Profi: 100 Stunden getrackt\n• Deadline-Held: 20 Tasks vor Deadline' },
                { title: '🎖️ Level-System', text: 'Level basiert auf Gesamtpunkten. Badges für Level 5, 10, 25, 50.' },
                { title: '🎁 Belohnungen', text: 'Admin erstellt Belohnungen (Name, Punkte, Emoji). Mitarbeiter können einlösen.' }
            ]
        },
        {
            id: 'benefits',
            icon: '🎫',
            title: 'Vorteilspass',
            color: '#AF52DE',
            content: [
                { title: '🎫 Kategorien', text: 'Food & Drinks, Fitness & Sport, Technik, Fashion, Beauty & Wellness, Entertainment, Reisen.' },
                { title: '💎 VIP Popup', text: 'Klick auf Benefit-Karte → Großer Rabatt, "AKTIV"-Badge, Mitarbeiter-Info. Beim Partner vorzeigen.' },
                { title: '📱 QR-Code System', text: 'Für Benefits mit Einlöse-Verifizierung:\n1. Admin aktiviert "QR-Scan erforderlich"\n2. Admin kann Einlöse-Limit pro Mitarbeiter festlegen\n3. Partner erhält QR-Code zum Ausdrucken' },
                { title: '📲 Scannen', text: '1. Benefit antippen → "QR scannen"\n2. Kamera erlauben\n3. QR-Code scannen\n4. Einlösung wird automatisch erfasst' },
                { title: '📊 Statistiken', text: 'Admins sehen: Wer hat eingelöst? Wann? Wie oft? Verbleibende Einlösungen.' },
                { title: '⚙️ Verwalten (Admin)', text: 'Hover über Karte → Edit/Delete. Felder: Name, Rabatt, Beschreibung, Logo, Kategorie, QR-Scan erforderlich, Max. Einlösungen.' }
            ]
        },
        {
            id: 'team',
            icon: '👨‍👩‍👧‍👦',
            title: 'Team Hub',
            color: '#00B4D8',
            content: [
                { title: '👥 Übersicht', text: 'Alle Teammitglieder mit Profilbild, Name, Rolle und Online-Status.' },
                { title: '🔍 Profil-Details', text: 'Klick auf Teammitglied → Skills, Kontaktinfo, aktuelle Tasks, Standort, Bio.' },
                { title: '⚡ Schnellaktionen', text: 'Direkt aus dem Profil: Chat starten, Anrufen, Task zuweisen.' },
                { title: '🟢 Auto-Status', text: 'Online-Status wird automatisch verwaltet:\n• Nach 30 Min Inaktivität → "Abwesend"\n• Bei Aktivität (Klick, Scrollen) → "Online"\n\nManueller Status bleibt erhalten.' }
            ]
        },
        {
            id: 'social-faces',
            icon: '🎭',
            title: 'Social Faces',
            color: '#EC4899',
            content: [
                { title: '🎭 Was ist das?', text: 'Die Talentdatenbank für Content-Produktionen. Models, Statisten, Influencer verwalten.' },
                { title: '📝 Bewerbung', text: '/social-faces - Öffentlicher Link für neue Talente mit Foto-Upload.' },
                { title: '🔍 Datenbank', text: 'Filter nach: Land, Geschlecht, Haarfarbe, Ernährung, Deutsch, Tattoos/Piercings/Brille.' },
                { title: '⚠️ Punktesystem', text: '1 Punkt = Verwarnung\n2 Punkte = Letzte Warnung\n3 Punkte = Blacklist' },
                { title: '📋 Zusagen', text: '/shooting-zusage - Talente bestätigen Shooting-Teilnahme mit Unterschrift.' }
            ]
        },
        {
            id: 'content-library',
            icon: '📚',
            title: 'Content Bibliothek',
            color: '#6366F1',
            content: [
                { title: '📚 Was ist das?', text: 'Zentrale Sammlung von Content-Ideen für Social Media. Gefiltert nach Branche, Format und Feiertagen.' },
                { title: '➕ Content erstellen', text: 'Titel, Beschreibung, Link (optional)\n\nAttribute:\n• Video / Bild / Grafik\n• Lustig / Informativ\n• Mit Reden / Ohne Reden\n• Branche (z.B. Gastronomie, Mode)\n• Feiertag zuordnen' },
                { title: '🔍 Filter', text: 'Nach Format, Kategorie und Feiertag filtern. Suchfunktion für schnelles Finden.' },
                { title: '📅 Content Kalender', text: 'Tab "Content Kalender" zeigt alle Feiertage:\n• Opernball, Valentinstag, Ostern\n• Star Wars Day, Muttertag, Pride\n• Halloween, Black Friday, Weihnachten\n\nKlick auf Feiertag → Filter nach zugehörigen Content-Ideen.' },
                { title: '⚙️ Branchen verwalten', text: 'Zahnrad-Icon (nur Admin) → Neue Branchen hinzufügen/löschen.' },
                { title: '📋 Berechtigungen', text: 'Jeder kann Content erstellen und eigene bearbeiten.\nNur Admin kann fremde Einträge löschen.' }
            ]
        },
        {
            id: 'influencers',
            icon: '📸',
            title: 'Influencer',
            color: '#E91E63',
            content: [
                { title: '📸 Was ist das?', text: 'Datenbank aller Influencer für Kooperationen. 134+ Influencer bereits vorinstalliert!' },
                { title: '📋 Datenfelder', text: '• Name / Instagram-Handle\n• Follower-Anzahl\n• Kategorien (Food, Lifestyle, Mode, Sport...)\n• Standort (Wien, Graz, Innsbruck...)\n• Kollaborations-Details (Preise)' },
                { title: '🔍 Filter & Suche', text: 'Filter nach Kategorie und Standort.\nSortierung nach Followern (auf-/absteigend) oder Name.' },
                { title: '📋 Kopieren', text: 'Copy-Button kopiert den Instagram-Link direkt in die Zwischenablage.' },
                { title: '⚙️ Verwalten (Admin)', text: 'Nur Admin kann Influencer hinzufügen, bearbeiten oder löschen.' }
            ]
        },
        {
            id: 'communications',
            icon: '💬',
            title: 'Kommunikation',
            color: '#10B981',
            content: [
                { title: '💬 Was ist das?', text: 'Vorlagen für WhatsApp-Nachrichten zum schnellen Kopieren und Versenden.' },
                { title: '📝 Vorlagen-Struktur', text: '• Titel/Name der Vorlage\n• Nachrichtentext (mit Platzhaltern wie [Name])\n• Kategorie (z.B. Begrüßung, Follow-Up, Angebot)' },
                { title: '📋 Kopieren', text: 'Klick auf "Kopieren" → Nachricht in Zwischenablage → In WhatsApp einfügen.' },
                { title: '🔍 Filter', text: 'Nach Kategorie filtern oder per Suche finden.' },
                { title: '⚙️ Kategorien verwalten', text: 'Zahnrad-Icon (nur Admin) → Kategorien hinzufügen/löschen.' },
                { title: '📋 Berechtigungen', text: 'Jeder kann Vorlagen sehen und kopieren.\nNur Admin kann Vorlagen erstellen/bearbeiten/löschen.' }
            ]
        },
        {
            id: 'public-links',
            icon: '🔗',
            title: 'Öffentliche Links',
            color: '#10B981',
            content: [
                { title: '📝 /social-faces', text: 'Bewerbungsformular für neue Talente. Weiterleitung zur WhatsApp-Gruppe.' },
                { title: '✅ /shooting-zusage', text: 'Shooting-Bestätigung für Kunden mit digitaler Unterschrift.' },
                { title: '📝 /einverstaendnis', text: 'Einverständniserklärung für Firmen-Mitarbeiter bei Shootings.' },
                { title: '📋 /client-intake', text: '20-Schritte Neukunden-Onboarding Formular.' },
                { title: '🌐 /p/{slug}', text: 'Individuelles Kunden-Portal mit CI, Links, Content-Plan und Reports.' }
            ]
        },
        {
            id: 'admin',
            icon: '🛡️',
            title: 'Admin',
            color: '#FF453A',
            content: [
                { title: '👤 Benutzer', text: 'Neuer Benutzer: E-Mail, Benutzername, Passwort, Vorname, Nachname, Rolle, Wochenstunden, Urlaubstage.' },
                { title: '📋 Task-Status', text: 'Status hinzufügen, Emoji und Farbe wählen, Reihenfolge per Drag & Drop.' },
                { title: '⚙️ Module', text: 'Design-Tab: Challenges, Team-Chat und Projekte ein-/ausschalten.\nDeaktivierte Module verschwinden aus dem Menü.' },
                { title: '⚙️ Einstellungen', text: 'Firmenname, Logo, Primärfarbe, Sekundärfarbe, Akzentfarbe, Standard-Theme, App-URL.' },
                { title: '📜 Audit-Log', text: 'Protokollierte Aktionen: Login, Benutzer, Kunden, Tasks, Zeiterfassung. Mit Filter und Suche.' },
                { title: '📧 E-Mails', text: 'Willkommensmails mit Zugangsdaten, Handbuch-Link.\nWöchentliche Zeitreports (Freitag).\nMonatliche Zeitübersicht (letzter Tag).' }
            ]
        }
    ];

    const filteredChapters = searchTerm 
        ? chapters.filter(ch => 
            ch.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ch.content.some(c => 
                c.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                c.text.toLowerCase().includes(searchTerm.toLowerCase())
            )
          )
        : chapters;

    const activeChapterData = chapters.find(ch => ch.id === activeChapter);

    const filteredContent = searchTerm && activeChapterData
        ? activeChapterData.content.filter(c => 
            c.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
            c.text.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : activeChapterData?.content || [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl w-[95vw] max-h-[100dvh] md:max-h-[85vh] p-0 overflow-hidden rounded-xl md:rounded-2xl border-0 shadow-2xl gap-0 [&>button]:hidden" style={{ zIndex: 60 }} data-testid="handbuch-modal">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b px-3 md:px-6 py-2.5 md:py-4">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <BookOpen className="w-4 h-4 md:w-6 md:h-6 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-base md:text-2xl font-bold">Handbuch</h2>
                            <p className="text-xs text-muted-foreground hidden sm:block">Dein Guide für AgencyOS</p>
                        </div>
                        {/* Search - Desktop */}
                        <div className="hidden md:block w-64 mr-8">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Suchen..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-10 py-2.5 text-sm rounded-2xl border bg-background/80 backdrop-blur focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                                />
                                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                {searchTerm && (
                                    <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-muted hover:bg-muted-foreground/20 flex items-center justify-center transition-colors">
                                        <X className="w-3 h-3 text-muted-foreground" />
                                    </button>
                                )}
                            </div>
                        </div>
                        {/* Close Button - always visible */}
                        <button
                            onClick={() => onOpenChange(false)}
                            className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center flex-shrink-0 transition-colors"
                            data-testid="handbuch-close-btn"
                        >
                            <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                    </div>
                    
                    {/* Mobile: Search + Chapter Dropdown */}
                    <div className="md:hidden mt-2.5 space-y-2">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Suchen..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-9 py-2 text-sm rounded-xl border bg-background/80 focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            {searchTerm && (
                                <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                                    <X className="w-3 h-3 text-muted-foreground" />
                                </button>
                            )}
                        </div>
                        {/* Chapter Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border bg-background/80 text-sm font-medium"
                                data-testid="handbuch-chapter-dropdown"
                            >
                                <span className="flex items-center gap-2">
                                    <span>{activeChapterData?.icon}</span>
                                    <span>{activeChapterData?.title}</span>
                                </span>
                                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {mobileMenuOpen && (
                                <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-xl border bg-background shadow-xl max-h-64 overflow-y-auto">
                                    {filteredChapters.map(chapter => (
                                        <button
                                            key={chapter.id}
                                            onClick={() => {
                                                setActiveChapter(chapter.id);
                                                setMobileMenuOpen(false);
                                            }}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors ${
                                                activeChapter === chapter.id
                                                    ? 'bg-primary/10 text-primary font-medium'
                                                    : 'hover:bg-muted/50'
                                            }`}
                                        >
                                            <span>{chapter.icon}</span>
                                            <span>{chapter.title}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Two Column Layout - Desktop / Single Column - Mobile */}
                <div className="flex h-[calc(100dvh-130px)] md:h-[calc(85vh-100px)]">
                    {/* Left Sidebar - Navigation (Desktop only) */}
                    <div className="hidden md:block w-56 border-r bg-muted/20 flex-shrink-0">
                        <ScrollArea className="h-full">
                            <div className="p-3 space-y-1">
                                {filteredChapters.map(chapter => (
                                    <button
                                        key={chapter.id}
                                        onClick={() => setActiveChapter(chapter.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                                            activeChapter === chapter.id
                                                ? 'bg-primary text-primary-foreground shadow-md'
                                                : 'hover:bg-muted/80 text-foreground'
                                        }`}
                                    >
                                        <span className="text-lg">{chapter.icon}</span>
                                        <span className="text-sm font-medium truncate">{chapter.title}</span>
                                    </button>
                                ))}
                            </div>
                        </ScrollArea>
                    </div>

                    {/* Right Content Area */}
                    <div className="flex-1 flex flex-col min-w-0">
                        {activeChapterData && (
                            <>
                                {/* Chapter Header - Desktop only */}
                                <div className="hidden md:block px-6 py-4 border-b bg-gradient-to-r from-muted/30 to-transparent">
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-xl flex items-center justify-center text-2xl"
                                            style={{ backgroundColor: `${activeChapterData.color}20` }}
                                        >
                                            {activeChapterData.icon}
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold">{activeChapterData.title}</h3>
                                            <p className="text-xs text-muted-foreground">{filteredContent.length} Einträge</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Content */}
                                <ScrollArea className="flex-1">
                                    <div className="p-3 md:p-6 flex flex-col gap-3">
                                        {filteredContent.length === 0 ? (
                                            <div className="text-center py-12 text-muted-foreground">
                                                <p className="text-4xl mb-3">🔍</p>
                                                <p>Keine Ergebnisse für "{searchTerm}"</p>
                                            </div>
                                        ) : (
                                            filteredContent.map((item, idx) => (
                                                <motion.div
                                                    key={idx}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: idx * 0.03 }}
                                                    className="p-3 md:p-4 rounded-xl bg-card border hover:shadow-md transition-all"
                                                >
                                                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                                        {item.title}
                                                    </h4>
                                                    <p className="text-xs md:text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                                                        {item.text}
                                                    </p>
                                                </motion.div>
                                            ))
                                        )}
                                    </div>
                                </ScrollArea>
                            </>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default HeaderBubble;
